import { ReactNode } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { InfoCard, Link } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { CATALOG_FILTER_EXISTS } from '@backstage/catalog-client';
import useAsync from 'react-use/esm/useAsync';
import AppsIcon from '@material-ui/icons/Apps';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import CodeIcon from '@material-ui/icons/Code';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import {
  boschBlue,
  boschGray,
} from '../../modules/theme/boschColors';

type Stat = {
  label: string;
  value: number;
  icon: ReactNode;
  url: string;
};

const useStyles = makeStyles(theme => ({
  statTile: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 4,
    border: `1px solid ${boschGray[85]}`,
    textDecoration: 'none',
    color: 'inherit',
    transition:
      'border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
      borderColor: boschBlue[50],
      transform: 'translateY(-1px)',
      boxShadow: theme.shadows[2],
      textDecoration: 'none',
    },
  },
  iconBox: {
    width: 40,
    height: 40,
    borderRadius: 4,
    backgroundColor: boschBlue[95],
    color: boschBlue[50],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  value: {
    fontSize: '1.5rem',
    fontWeight: 700,
    lineHeight: 1.1,
    color: boschGray[15],
  },
  label: {
    fontSize: '0.7rem',
    color: boschGray[50],
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontWeight: 600,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 180,
  },
}));

export const CatalogStats = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);

  const { value: counts, loading } = useAsync(async () => {
    const [components, systems, apis, techdocs] = await Promise.all([
      catalogApi.getEntities({
        filter: { kind: 'Component' },
        fields: ['metadata.name'],
      }),
      catalogApi.getEntities({
        filter: { kind: 'System' },
        fields: ['metadata.name'],
      }),
      catalogApi.getEntities({
        filter: { kind: 'API' },
        fields: ['metadata.name'],
      }),
      catalogApi.getEntities({
        filter: {
          'metadata.annotations.backstage.io/techdocs-ref':
            CATALOG_FILTER_EXISTS,
        },
        fields: ['metadata.name'],
      }),
    ]);
    return {
      components: components.items.length,
      systems: systems.items.length,
      apis: apis.items.length,
      techdocs: techdocs.items.length,
    };
  }, [catalogApi]);

  const stats: Stat[] = [
    {
      label: 'Components',
      value: counts?.components ?? 0,
      icon: <AppsIcon />,
      url: '/catalog?filters%5Bkind%5D=component',
    },
    {
      label: 'Systems',
      value: counts?.systems ?? 0,
      icon: <AccountTreeIcon />,
      url: '/catalog?filters%5Bkind%5D=system',
    },
    {
      label: 'APIs',
      value: counts?.apis ?? 0,
      icon: <CodeIcon />,
      url: '/catalog?filters%5Bkind%5D=api',
    },
    {
      label: 'TechDocs',
      value: counts?.techdocs ?? 0,
      icon: <LibraryBooksIcon />,
      url: '/docs',
    },
  ];

  return (
    <InfoCard title="Catalog Overview">
      {loading ? (
        <Box className={classes.loading}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {stats.map(stat => (
            <Grid key={stat.label} item xs={6}>
              <Link to={stat.url} className={classes.statTile}>
                <Box className={classes.iconBox}>{stat.icon}</Box>
                <Box>
                  <Typography className={classes.value}>
                    {stat.value}
                  </Typography>
                  <Typography className={classes.label}>
                    {stat.label}
                  </Typography>
                </Box>
              </Link>
            </Grid>
          ))}
        </Grid>
      )}
    </InfoCard>
  );
};
